import { GAME_ASSET_SLUG } from "@/config/game";
import { useFootLabel } from "@/hooks/useFootLabel";
import {
  contentLabelsSelector,
  getCountriesSelector,
  getVersionAssetsSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import {
  getClubsSelector,
  getLeaguesSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { bodyTypeIdToName } from "@/redux/squadBuilder/squadBuilderUtils";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { cmToFeetAndInches, kgToPounds } from "@repo/shared/utils";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { GridBox } from "../DetailedPlayer";
import { Row } from "./components/Row";

export interface PlayerBioProps {
  displayName: string;
  countryId: number;
  clubId: number;
  leagueId: number;
  possiblePositions: string[];
  cardType: string;
  versionId: number;
  bodyTypeId: number;
  skillMoves: number;
  weakFoot: number;
  preferredFoot: string;
  weight: number;
  height: number;
}

export const PlayerBio = ({
  displayName,
  countryId,
  clubId,
  leagueId,
  possiblePositions,
  cardType,
  versionId,
  bodyTypeId,
  skillMoves,
  weakFoot,
  preferredFoot,
  weight,
  height,
}: PlayerBioProps) => {
  const country = useSelector(getCountriesSelector(countryId))?.name;
  const { positions } = useSelector(contentLabelsSelector);
  const league = useSelector(getLeaguesSelector(leagueId))?.name;
  const club = useSelector(getClubsSelector(clubId))?.name;
  const assetsBaseUrl = import.meta.env.VITE_APP_ASSETS_BASE_URL;
  const versionAssets = useSelector(getVersionAssetsSelector);
  const version = versionAssets[versionId]?.name;
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const { t } = useTranslation("players");
  const footLabel = useFootLabel();
  return (
    <GridBox
      title={t("detailed.bio.title")}
      className="col-span-4 touch-device:col-span-2 touch-device:min-h-[340px]"
    >
      <div className="flex flex-col mobile:flex-row w-full gap-x-4 mt-4 mobile:mt-0">
        <div className="flex flex-col w-full">
          <Row
            title={t("detailed.bio.name")}
            value={displayName}
            {...(isMobile && { stretch: true })}
          />
          <Row
            title={t("detailed.bio.country")}
            value={
              countryId && (
                <div className="flex flex-row gap-x-1 items-center">
                  <img
                    src={
                      assetsBaseUrl +
                      `/${GAME_ASSET_SLUG}/countries/` +
                      countryId +
                      ".png"
                    }
                    alt={country}
                    className="h-3"
                  />
                  <p>{country}</p>
                </div>
              )
            }
            {...(isMobile && { stretch: true })}
          />
          <Row
            title={t("detailed.bio.club")}
            value={
              clubId && (
                <div className="flex flex-row gap-x-1 items-center">
                  <img
                    src={
                      assetsBaseUrl +
                      `/${GAME_ASSET_SLUG}/clubs/dark/` +
                      clubId +
                      ".png"
                    }
                    alt={club}
                    className="mobile:h-4 h-3"
                  />
                  <p>{club}</p>
                </div>
              )
            }
            {...(isMobile && { stretch: true })}
          />
          <Row
            title={t("detailed.bio.league")}
            value={
              leagueId && (
                <div className="flex flex-row gap-x-1">
                  <img
                    src={
                      assetsBaseUrl +
                      `/${GAME_ASSET_SLUG}/leagues/dark/` +
                      leagueId +
                      ".png"
                    }
                    alt={league}
                    className="mobile:h-4 h-3"
                  />
                  <p>{league}</p>
                </div>
              )
            }
            {...(isMobile && { stretch: true })}
          />
          <Row
            title={t("detailed.bio.positions")}
            value={possiblePositions
              ?.map((p) => positions[p]?.name ?? p)
              .join(", ")}
            {...(isMobile && { stretch: true })}
          />
          <Row
            title={t("detailed.bio.cardType")}
            value={cardType}
            {...(isMobile && { stretch: true })}
          />

          <Row
            title={t("detailed.bio.version")}
            value={version}
            hiddenBorder={!isMobile}
            {...(isMobile && { stretch: true })}
          />
        </div>
        <div className="flex flex-col gap-2 w-full">
          <Row
            title={t("detailed.bio.bodyType")}
            value={bodyTypeIdToName[bodyTypeId]?.[0]}
            stretch
          />
          <Row
            title={t("detailed.bio.skillMoves")}
            value={skillMoves}
            stretch
          />
          <Row title={t("detailed.bio.weakFoot")} value={weakFoot} stretch />
          <Row
            title={t("detailed.bio.foot")}
            value={footLabel(preferredFoot)}
            stretch
          />
          <Row
            title={t("detailed.bio.height")}
            value={
              height && (
                <div className="flex flex-row gap-x-1">
                  <span>{height / 100 + "m"}</span>
                  <span>({cmToFeetAndInches(height)})</span>
                </div>
              )
            }
            stretch
          />
          <Row
            title={t("detailed.bio.weight")}
            value={
              weight && (
                <div className="flex flex-row gap-x-1">
                  <span>{weight + "kg"}</span>
                  <span>({kgToPounds(weight)})</span>
                </div>
              )
            }
            hiddenBorder
          />
        </div>
      </div>
    </GridBox>
  );
};
